import * as d3 from "d3";
import { scaleD3Chart, margin, width, height } from "./chart";
import { commafy, toTitleCase, timeout } from "./utilities";
import { drawLegend } from "./legend";

// D3 Vars
const chartDiv = document.getElementById("chart");

// wrap long text
function wrap(text, width) {
  text.each(function () {
    var text, words, word, line, lineNumber, lineHeight, y, dy, tspan;
    text = d3.select(this);
    words = text.text().split(/\s+/).reverse();
    line = [];
    lineNumber = 0;
    lineHeight = 1.1;
    y = text.attr("y");
    dy = parseFloat(text.attr("dy"));
    tspan = text
      .text(null)
      .append("tspan")
      .attr("x", 0)
      .attr("y", y)
      .attr("dy", dy + "em");
    while ((word = words.pop())) {
      line.push(word);
      tspan.text(line.join(" "));
      if (tspan.node().getComputedTextLength() > width) {
        line.pop();
        tspan.text(line.join(" "));
        line = [word];
        tspan = text
          .append("tspan")
          .attr("x", 0)
          .attr("y", y)
          .attr("dy", function () {
            return ++lineNumber * lineHeight + dy + "em";
          })
          .text(word);
      }
    }
  });
}

export function clearLabels() {
  let svg = d3.select(chartDiv).select("svg");
  svg.select(".yaxis").selectAll(".yaxislabel").remove();
  svg.select(".yaxis").selectAll(".tick").remove();
}

export function removeNotificationTextfromChart() {
  let svg = d3.select(chartDiv).select("svg");
  //remove any notification text
  svg
    .select("g.chart")
    .select("g.contents")
    .selectAll("g.notifications")
    .selectAll("text")
    .remove();
}

function reDrawYAxisLabels() {
  let svg = d3.select(chartDiv).select("svg");
  //redraw axis text
  svg.select(".yaxislabel").selectAll("text").remove();
  svg
    .select(".yaxislabel")
    .append("text")
    .attr("class", "yaxislabel")
    .attr("transform", "rotate(-90)")
    .attr("x", -80)
    .attr("y", -45)
    .attr("dy", ".71em")
    .attr("fill", "#000")
    .attr("text-anchor", "end")
    .attr("font-family", "Helvetica, Arial, sans-serif;")
    .attr("font-weight", 400)
    .attr("line-height", 1.5)
    .text("Annual Earnings of Degree Holders (2023 Dollars)");
}

export function addSelectDegreeTexttoChart() {
  let width = +chartDiv.clientWidth - margin.left - margin.right;
  let height = +chartDiv.clientHeight - margin.top - margin.bottom;

  let svg = d3.select(chartDiv).select("svg");
  svg
    .select("g.chart")
    .select("g.contents")
    .append("g")
    .attr("class", "notifications")
    .attr("transform", "translate(" + width / 2 + "," + height / 2 + ")")
    .append("text")
    .attr("font-family", "Helvetica, Arial, sans-serif;")
    .attr("font-weight", "bold")
    .attr("fill", "#6c757d")
    .attr("text-anchor", "middle")
    .text("Select one or more fields of study below");
}

// generate collection of D3 scales for charting
function getScales(d3DataObj, analysisType) {
  return new Promise((resolve, reject) => {
    let scale;
    let keys;
    let scale_lookup = {};

    if (analysisType === "earnings-percentile") {
      scale = ["#2E78D2", "#205493", "#112E51"];
      keys = ["1", "5", "10"];
    } else {
      scale = ["#FF7043", "#C25432", "#853A22"];
      keys = ["25", "50", "75"];
    }

    //hardcoded color lookup
    for (var i = 0; i < keys.length; i++) {
      scale_lookup[keys[i]] = scale[i];
    }

    //scales
    var x0 = d3
      .scaleBand()
      .rangeRound([0, width])
      .paddingInner(0.1)
      .paddingOuter(0.1)
      .domain(d3DataObj.ids);

    var labelx = d3
      .scaleBand()
      .rangeRound([0, width])
      .paddingInner(0.1)
      .paddingOuter(0.1)
      .domain(d3DataObj.labels);

    var x1 = d3
      .scaleBand()
      .padding(0.05)
      .domain(d3DataObj.keys)
      .rangeRound([0, x0.bandwidth()]);

    var y = d3
      .scaleLinear()
      .rangeRound([height, 0])
      .domain([0, d3DataObj.maxVal])
      .nice();

    var z = d3.scaleOrdinal().range(scale);

    resolve({
      labelx: labelx,
      x0: x0,
      x1: x1,
      y: y,
      z: z,
      z_lookup: scale_lookup,
    });
  });
}

function labelOpacity(cipCodeFilter) {
  // turn off bar labels if >= 8 degrees selected
  let retval = 1;
  if (cipCodeFilter && cipCodeFilter.length > 8) {
    retval = 0;
  }
  return retval;
}

export function clearBarChartArea() {
  let svg = d3.select(chartDiv).select("svg");
  let chart = svg.select("g.chart");
  chart.select("g.contents").selectAll("g.bargroup").remove();
  chart.select("g.yaxis").selectAll("*").remove();
  chart.select("g.xaxis").selectAll("*").remove();
  chart.select("g.labels").selectAll("*").remove();
}

export async function updateD3BarChart(pseoData) {
  let svg = d3.select(chartDiv).select("svg");

  // if no degrees are selected clear the chart
  if (pseoData.cipCodeFilter.length === 0) {
    clearBarChartArea();
    addSelectDegreeTexttoChart();
    return;
  } else {
    removeNotificationTextfromChart();
  }

  reDrawYAxisLabels();

  //wrap the whole thing in a queued function incase of rapid call
  await timeout(25);

  // get and format the most current data from the UI
  const d3Data = await formatD3Data(pseoData);

  // create the scales based on data and window size
  const d3Scales = await getScales(d3Data, pseoData.analysis_type);

  let transitionDuration = 400;

  let bg = svg
    .select("g.chart")
    .select("g.contents")
    .selectAll("g.bargroup")
    .data(d3Data.data, (d) => d.cipcode);

  //EXIT old elements not present in new data
  let bgexit = bg.exit();

  bgexit
    .selectAll("rect")
    .transition()
    .duration(transitionDuration)
    .style("opacity", 0)
    .attr("height", 0)
    .attr("y", function () {
      return d3Scales.y(height);
    })
    .remove();

  bgexit.transition().delay(transitionDuration).remove();

  //ENTER new elements
  let bgenter = bg.enter();

  bgenter
    .append("g")
    .attr("class", "bargroup")
    .attr("transform", function (d) {
      return "translate(" + d3Scales.x0(d.cipcode) + ",0)";
    })
    .selectAll("rect")
    .data(function (d) {
      return d3Data.keys.map(function (key) {
        return { key: key, value: d[key] || 0 };
      });
    })
    .enter()
    .append("rect")
    .attr("x", function (d) {
      return d3Scales.x1(d.key);
    })
    .attr("y", function () {
      return d3Scales.y(height);
    })
    .attr("width", d3Scales.x1.bandwidth())
    .attr("height", 0)
    .transition()
    .duration(transitionDuration)
    .style("opacity", 1)
    .attr("y", function (d) {
      return d3Scales.y(d.value);
    })
    .attr("height", function (d) {
      return height - d3Scales.y(d.value);
    })
    .attr("fill", function (d) {
      return d3Scales.z_lookup[d.key];
    });

  //make room in the bar but allow a delay for other transitions
  bg.transition()
    .duration(transitionDuration)
    .attr("transform", function (d) {
      return "translate(" + d3Scales.x0(d.cipcode) + ",0)";
    });

  //UPDATE existing data
  var bgUpdate = bg.selectAll("rect").data(function (d) {
    return d3Data.keys.map(function (key) {
      return { key: key, value: d[key] || 0 };
    });
  });

  //if there are less bars than the previous
  bgUpdate
    .exit()
    .transition()
    .duration(transitionDuration)
    .style("opacity", 0)
    .attr("height", 0)
    .attr("y", function () {
      return d3Scales.y(height);
    })
    .remove();

  // if there are more bars than previous
  bgUpdate
    .enter()
    .append("rect")
    .attr("x", function (d) {
      return d3Scales.x1(d.key);
    })
    .attr("y", function () {
      return d3Scales.y(height);
    })
    .attr("width", d3Scales.x1.bandwidth())
    .attr("height", 0)
    .transition()
    .duration(transitionDuration)
    .style("opacity", 1)
    .attr("y", function (d) {
      return d3Scales.y(d.value);
    })
    .attr("height", function (d) {
      return height - d3Scales.y(d.value);
    })
    .attr("fill", function (d) {
      return d3Scales.z_lookup[d.key];
    });

  // if there are the same number of bars as previous
  bgUpdate
    .transition()
    .style("opacity", 1)
    .attr("x", function (d) {
      return d3Scales.x1(d.key);
    })
    .attr("y", function (d) {
      return d3Scales.y(d.value);
    })
    .attr("width", d3Scales.x1.bandwidth())
    .attr("height", function (d) {
      return height - d3Scales.y(d.value);
    })
    .attr("fill", function (d) {
      return d3Scales.z_lookup[d.key];
    });

  //labels
  var lg = svg
    .select("g.chart")
    .select("g.labels")
    .selectAll("g.labelgroup")
    .data(d3Data.data, function (d) {
      return d.cipcode;
    });

  //EXIT old elements not present in new data
  let lgexit = lg.exit();

  lgexit
    .selectAll("text")
    .transition()
    .duration(transitionDuration)
    .attr("height", 0)
    .attr("y", function () {
      return d3Scales.y(height);
    })
    .style("opacity", 0)
    .remove();

  lgexit.transition().delay(transitionDuration).remove();

  // Label specific variables
  const labelPadding = 4;
  const labelFontSizePx = (d3Scales.x1.bandwidth() / 5).toFixed();
  const labelYPosition = (d) => {
    const spaceNeededForLabel = parseInt(labelFontSizePx) + labelPadding;
    const spaceAvailableInBar = parseInt(height - d3Scales.y(d.value));

    // show label inside
    if (spaceNeededForLabel <= spaceAvailableInBar) {
      return d3Scales.y(d.value) + spaceNeededForLabel;
    }
    // show label above bar
    return d3Scales.y(d.value) - labelPadding;
  };
  const labelColor = (d) => {
    const spaceNeededForLabel = parseInt(labelFontSizePx) + labelPadding;
    const spaceAvailableInBar = parseInt(height - d3Scales.y(d.value));

    // label inside bar
    if (spaceNeededForLabel <= spaceAvailableInBar) {
      return "white";
    }
    // label above bar colored the same as the bar it represents
    return d3Scales.z_lookup[d.key];
  };

  //ENTER new elements
  lg.enter()
    .append("g")
    .attr("class", "labelgroup")
    .attr("transform", function (d) {
      return "translate(" + d3Scales.x0(d.cipcode) + ",0)";
    })
    .selectAll("text")
    .data(function (d) {
      return d3Data.keys.map(function (key) {
        return { key: key, value: d[key] || 0 };
      });
    })
    .enter()
    .append("text")
    .text(function (d) {
      if (d.value !== 0) {
        return "$" + d3.format(",.0f")(d.value);
      }
    })
    .attr("font-family", "Helvetica, Arial, sans-serif;")
    .attr("font-weight", "bold")
    .attr("fill", "white")
    .attr("text-anchor", "middle")
    .style("opacity", 0)
    .attr("x", function (d) {
      return d3Scales.x1(d.key) + d3Scales.x1.bandwidth() / 2;
    })
    .attr("y", function () {
      return d3Scales.y(height);
    })
    .attr("font-size", function () {
      return labelFontSizePx;
    })
    .transition()
    .duration(transitionDuration)
    .attr("fill", (d) => labelColor(d))
    .style("opacity", labelOpacity(pseoData.cipCodeFilter))
    .attr("y", function (d) {
      return labelYPosition(d);
    });

  lg.transition()
    .duration(transitionDuration)
    .attr("transform", function (d) {
      return "translate(" + d3Scales.x0(d.cipcode) + ",0)";
    });

  //UPDATE existing data
  var lgUpdate = lg.selectAll("text").data(function (d) {
    return d3Data.keys.map(function (key) {
      return { key: key, value: d[key] || 0 };
    });
  });

  //if there are less bars than the previous
  lgUpdate
    .exit()
    .transition()
    .duration(transitionDuration)
    .attr("height", 0)
    .attr("y", function () {
      return d3Scales.y(height);
    })
    .style("opacity", 0)
    .remove();

  // if there are more bars than previous
  lgUpdate
    .enter()
    .append("text")
    .text(function (d) {
      if (d.value !== 0) {
        return "$" + d3.format(",.0f")(d.value);
      }
    })
    .attr("font-family", "Helvetica, Arial, sans-serif;")
    .attr("font-weight", "bold")
    .attr("text-anchor", "middle")
    .style("opacity", 0)
    .attr("x", function (d) {
      return d3Scales.x1(d.key) + d3Scales.x1.bandwidth() / 2;
    })
    .attr("y", function () {
      return d3Scales.y(height);
    })
    .attr("font-size", function () {
      return labelFontSizePx;
    })
    .transition()
    .duration(transitionDuration)
    .style("opacity", labelOpacity(pseoData.cipCodeFilter))
    .attr("fill", (d) => labelColor(d))
    .attr("y", function (d) {
      return labelYPosition(d);
    });

  // if there are the same number of bars as previous
  lgUpdate
    .text(function (d) {
      if (d.value !== 0) {
        return "$" + d3.format(",.0f")(d.value);
      }
    })
    .transition()
    .attr("x", function (d) {
      return d3Scales.x1(d.key) + d3Scales.x1.bandwidth() / 2;
    })
    .attr("y", function (d) {
      return labelYPosition(d);
    })
    .attr("font-family", "Helvetica, Arial, sans-serif;")
    .attr("font-size", function () {
      return labelFontSizePx;
    })
    .attr("font-weight", "bold")
    .attr("fill", (d) => labelColor(d))
    .attr("text-anchor", "middle")
    .style("opacity", labelOpacity(pseoData.cipCodeFilter));

  //transition yaxis
  svg
    .select(".yaxis")
    .transition()
    .duration(transitionDuration)
    .call(d3.axisLeft(d3Scales.y).ticks(null, "s"));

  //transition xaxis
  svg
    .select(".xaxis")
    .attr("transform", "translate(0," + height + ")")
    .call(d3.axisBottom(d3Scales.labelx))
    .selectAll(".tick text")
    .transition()
    .duration(transitionDuration)
    .attr("font-family", "Helvetica, Arial, sans-serif;")
    .call(wrap, d3Scales.labelx.bandwidth());

  // add tooltips
  var chart = d3.select(".chartcontainer");
  chart.selectAll(".tooltip").remove();
  var tooltip = chart
    .append("div")
    .attr("class", "tooltip")
    .style("opacity", 0);

  svg
    .select("g.chart")
    .select("g.contents")
    .selectAll("g.bargroup")
    .selectAll("rect")
    .on("mousemove", function (d) {
      tooltip.transition().duration(200).style("opacity", 1);

      tooltip
        .html(function () {
          // format money, no fractions
          return "$" + commafy(d.value.toFixed(0));
        })
        .style("left", function () {
          return d3.event.pageX + 14 + "px";
        })
        .style("top", function () {
          return d3.event.pageY - 21 + "px";
        });
    })
    .on("mouseout", function () {
      tooltip.transition().duration(500).style("opacity", 0);
    })
    .on("mouseover", function () {
      tooltip.transition().duration(500).style("opacity", 0);
    });

  drawLegend(d3Data, d3Scales, pseoData.analysis_type);
  //}, 25);
}

// draw d3 svg
export function initD3BarChart() {
  let svg = d3.select(chartDiv).select("svg");

  svg.select("g.chart").selectAll("*").remove();

  //scale the svg to the area
  scaleD3Chart();

  let chart = svg.select("g.chart");
  chart.append("g").attr("class", "contents");
  chart.append("g").attr("class", "yaxis");
  chart.append("g").attr("class", "yaxislabel");
  chart.append("g").attr("class", "xaxis");
  chart.append("g").attr("class", "labels");

  let twoToggleDiv = $j33("#twotoggle").parent();
  if (twoToggleDiv.hasClass("d-none")) {
    twoToggleDiv.removeClass("d-none");
  }
  let threeToggleDiv = $j33("#threetoggle").parent();
  if (threeToggleDiv.hasClass("d-none")) {
    threeToggleDiv.removeClass("d-none");
  }
  let flowsToggleDiv = $j33("#flowstoggle").parent();
  flowsToggleDiv.addClass("d-none");
  let flowsDisplayDiv = $j33("#flows-display").parent();
  flowsDisplayDiv.addClass("d-none");

  let flowsSelectedCutDiv = $j33("#flows-selected-cut").parent();
  flowsSelectedCutDiv.addClass("d-none");

  let legendDiv = $j33("#legend").parent();
  if (legendDiv.hasClass("d-none")) {
    legendDiv.removeClass("d-none");
  }
}

function derangementNumber(n) {
  if (n == 0) {
    return 1;
  }
  var factorial = 1;
  while (n) {
    factorial *= n--;
  }
  return Math.floor(factorial / Math.E);
}

function derange(array) {
  array = array.slice();
  var mark = array.map(function () {
    return false;
  });
  for (var i = array.length - 1, u = array.length - 1; u > 0; i--) {
    if (!mark[i]) {
      var unmarked = mark
        .map(function (_, i) {
          return i;
        })
        .filter(function (j) {
          return !mark[j] && j < i;
        });
      var j = unmarked[Math.floor(Math.random() * unmarked.length)];

      var tmp = array[j];
      array[j] = array[i];
      array[i] = tmp;

      // this introduces the unbiased random characteristic
      if (
        Math.random() <
        (u * derangementNumber(u - 1)) / derangementNumber(u + 1)
      ) {
        mark[j] = true;
        u--;
      }
      u--;
    }
  }
  return array;
}

export function hackyCohortFix() {
  let svg = d3.select(chartDiv).select("svg");
  //hacky fix for cohort bug where 1st element can't clear rect/text on exit()
  svg.select("g.chart").select("g.contents").selectAll("g.bargroup").remove();
  svg.select("g.chart").select("g.labels").selectAll("g.labelgroup").remove();
}

export function clearBarLoader(animationTimer) {
  let svg = d3.select(chartDiv).select("svg");
  // turn off the loading animation
  clearInterval(animationTimer);
  animationTimer = null;
  svg.select("g.chart").select("g.barloader").remove();
}

export function clearBarChart() {
  let svg = d3.select(chartDiv).select("svg");
  clearAxis();

  svg.select("g.chart").select("g.contents").selectAll("g.bargroup").remove();

  // clear the institution button
  let dropdownButton = $j33("#institution-dropdown-button");
  dropdownButton.text("Select Institution");
  if (dropdownButton.hasClass("active")) {
    dropdownButton.removeClass("active");
  }
  dropdownButton.prop("disabled", true);

  // clear the degreelevel button
  dropdownButton = $j33("#degreelevel-dropdown-button");
  dropdownButton.text("Select DegreeLevel");
  if (dropdownButton.hasClass("active")) {
    dropdownButton.removeClass("active");
  }
  dropdownButton.prop("disabled", true);
}

export function clearAxis() {
  let svg = d3.select(chartDiv).select("svg");
  svg.select("g.chart").select("g.xaxis").selectAll("g.tick").remove();
  svg.select("g.chart").select("g.yaxis").selectAll("g.tick").remove();
  svg.select(".yaxis").selectAll(".yaxislabel").remove();
}

export function drawBarLoader(animationTimer) {
  clearBarLoader(animationTimer); //re initialize the bar loader

  let svg = d3.select(chartDiv).select("svg");

  let scale1 = ["#2E78D2", "#205493", "#112E51"],
    scale2 = ["#FF7043", "#C25432", "#853A22"];

  let miniwidth = 100,
    miniheight = 100;

  let minidata = d3.range(1, 4, 1);

  let minix = d3
    .scaleBand()
    .padding(0.05)
    .domain([1, 2, 3])
    .rangeRound([0, miniwidth]);

  let miniy = d3
    .scaleLinear()
    .rangeRound([miniheight, 0])
    .domain([0, 3])
    .nice();

  let miniz1 = d3.scaleOrdinal().range(scale1);

  let miniz2 = d3.scaleOrdinal().range(scale2);

  let miniz = miniz1;

  let g = svg
    .select("g.chart")
    .append("g")
    .attr("class", "barloader")
    .selectAll("rect")
    .data(minidata)
    .enter()
    .append("rect")
    .attr("x", function (d) {
      return minix(d);
    })
    .attr("y", function (d) {
      return miniy(d);
    })
    .attr("width", minix.bandwidth())
    .attr("height", function (d) {
      return miniheight - miniy(d);
    })
    .attr("fill", function (d) {
      return miniz(d);
    })
    .style("opacity", 1)
    .attr("transform", function (d) {
      return (
        "translate(" +
        (width - margin.left - miniwidth / 2) / 2 +
        "," +
        (height - margin.top - miniheight / 2) / 2 +
        ")"
      );
    });

  // state label
  svg
    .select("g.chart")
    .select("g.barloader")
    .append("g")
    .attr("class", "loadingtext")
    .attr("width", miniwidth)
    .attr(
      "transform",
      "translate(" +
        (width - margin.left + miniwidth / 2) / 2 +
        "," +
        (height - margin.top + miniheight * 2) / 2 +
        ")"
    )
    .append("text")
    .attr("font-family", "Helvetica, Arial, sans-serif;")
    .attr("font-weight", "bold")
    .attr("fill", "#6c757d")
    .attr("text-anchor", "middle")
    .text(function () {
      if ($j33("#dropdownStateButton").text() != "Select State") {
        return (
          "Loading " +
          toTitleCase($j33("#dropdownStateButton").text()) +
          " Data..."
        );
      } else {
        return "Loading...";
      }
    });

  // subtext label
  svg
    .select("g.chart")
    .select("g.barloader")
    .select("g.loadingtext")
    .append("g")
    .attr("class", "loadingsubtext")
    .attr("width", miniwidth)
    .append("text")
    .attr("y", 18)
    .attr("font-family", "Helvetica, Arial, sans-serif;")
    .attr("font-weight", "bold")
    .attr("fill", "#a0a6ac")
    .attr("text-anchor", "middle")
    .text(""); // removed university tagline by request

  let timer = 45;
  animationTimer = setInterval(function () {
    minidata = derange(minidata);
    miniz = miniz == miniz1 ? miniz2 : miniz1;
    svg
      .select("g.chart")
      .select("g.barloader")
      .selectAll("rect")
      .data(minidata)
      .transition()
      .duration(750)
      .attr("y", function (d) {
        return miniy(d);
      })
      .attr("height", function (d) {
        return miniheight - miniy(d);
      })
      .attr("fill", function (d, i) {
        return miniz(i);
      });

    var subtext = svg
      .select("g.chart")
      .select("g.barloader")
      .select("g.loadingtext")
      .select("g.loadingsubtext")
      .select("text");
    if (--timer == 40) {
      subtext.text("PSEO data are still loading");
    } else if (timer == 35) {
      subtext.text("Sorry, this is taking longer than expected");
    } else if (timer == 20) {
      subtext.text("Wait a few more seconds then try to refresh the page");
    } else if (timer == 0) {
      subtext.text(
        "The request timed out. Email us: CES.PSEO.Feedback@census.gov"
      );
    }
  }, 750);

  return animationTimer;
}

function formatD3Data({
  cipCodeFilter,
  analysis_type,
  cipCodeData,
  selected_cut,
}) {
  return new Promise((resolve, reject) => {
    // format the data for use by d3, return a list of objects to be rendered

    // object to be returned
    let formattedData = {
      ids: [], // bar groups
      labels: [],
      data: [], // data formatted for d3
      maxVal: 0, // max Y value for charting
      keys: [], // keys used with the data
    };

    // get array of cip ids and labels, paried and ordered for labeling
    cipCodeFilter.forEach((f) => {
      formattedData.ids.push(f.cipCode);
      formattedData.labels.push(f.cipCodeLabel);
    });

    if (analysis_type === "earnings-percentile") {
      // loop through all of the degrees and format only the needed columns
      cipCodeData.forEach((d) => {
        const item = (({ cipcode, cipcode_label }) => ({
          cipcode,
          cipcode_label,
        }))(d); //equivalent to _.pick

        //loop through each option for year postgrad, append to item if exists
        let yearPostgradArr = ["1", "5", "10"];
        yearPostgradArr.forEach((y) => {
          let key = "y" + y + "_p" + selected_cut + "_earnings";

          if (d[key]) {
            item[y] = d[key];
            // add the percentile to the keys if not already there
            if (
              !formattedData.keys.some((k) => {
                return k == y;
              })
            ) {
              formattedData.keys.push(y);
            }

            // update max val if necessary
            if (item[y] > formattedData.maxVal) {
              formattedData.maxVal = item[y];
            }
          }
        });
        //add all the data to the list
        formattedData.data.push(item);
      });
      // years-postgrad data needs 25,50,and/or75 grouped by years postgrad (1 default)
    } else if (analysis_type === "years-postgrad") {
      // loop through all of the degrees and extract only the needed columns
      cipCodeData.forEach((d) => {
        // add the degree name to every set of data
        const item = (({ cipcode, cipcode_label }) => ({
          cipcode,
          cipcode_label,
        }))(d); //equivalent to _.pick

        //loop through each option for year postgrad, append to item if exists
        let percentileArr = ["25", "50", "75"];
        percentileArr.forEach((p) => {
          let key = "y" + selected_cut + "_p" + p + "_earnings";

          if (d[key]) {
            item[p] = d[key];
            // add the percentile to the keys if not already there
            if (
              !formattedData.keys.some((k) => {
                return k == p;
              })
            ) {
              formattedData.keys.push(p);
            }

            // update max val if necessary
            if (item[p] > formattedData.maxVal) {
              formattedData.maxVal = item[p];
            }
          }
        });

        formattedData.data.push(item);
      });
    }

    if (!formattedData.keys.some(isNaN)) {
      formattedData.keys.sort(function (a, b) {
        return a - b;
      });
    } else {
      formattedData.keys.sort();
    }

    return resolve(formattedData);
  });
}
